// ─── Server → Client events ─────────────────────────────────────────────────

export interface WsConnectedEvent {
    kind: 'connected';
}

export interface WsReadyEvent {
    kind: 'ready';
}

export interface WsSessionsEvent {
    kind: 'sessions';
    sessions: WsSessionInfo[];
    /** Есть ли более старая страница списка. */
    hasMore: boolean;
    /** Эхо курсора запроса; нет на первой странице. */
    before?: string;
}

export interface WsSessionReadyEvent {
    kind: 'session_ready';
    sessionId: string;
}

export interface WsSessionClosedEvent {
    kind: 'session_closed';
    sessionId: string;
}

export interface WsSessionTitleEvent {
    kind: 'session_title';
    sessionId: string;
    title: string;
}

export interface WsHistoryEvent {
    kind: 'history';
    messages: WsHistoryMessage[];
    /**
     * Для запроса без курсора / с `before` — есть ли более старые сообщения.
     * Для запроса с `after` — есть ли более новые.
     */
    hasMore: boolean;
    /** Эхо `before` из запроса. */
    before?: string;
    /** Эхо `after` из запроса. */
    after?: string;
}

export interface WsMessageStartEvent {
    kind: 'message_start';
    messageId: string;
}

export interface WsDeltaEvent {
    kind: 'delta';
    messageId: string;
    text: string;
}

export interface WsToolStartEvent {
    kind: 'tool_start';
    messageId: string;
    runId: string;
    parentRunId: string | null;
    name: string;
    input: unknown;
    label?: string;
}

export interface WsToolDeltaEvent {
    kind: 'tool_delta';
    messageId: string;
    runId: string;
    text: string;
}

export interface WsToolEndEvent {
    kind: 'tool_end';
    messageId: string;
    runId: string;
    name: string;
    output: unknown;
}

export interface WsMessageEndEvent {
    kind: 'message_end';
    messageId: string;
    text: string;
    blocks?: WsBlock[];
}

export interface WsMessageCancelEvent {
    kind: 'message_cancel';
    messageId: string;
}

export interface WsUserMessageEvent {
    kind: 'user_message';
    text: string;
}

export interface WsErrorEvent {
    kind: 'error';
    messageId?: string;
    error: string;
    code?: string;
}

export interface WsToolRequestEvent {
    kind: 'tool_request';
    toolCallId: string;
    toolName: string;
    args: Record<string, unknown>;
}

export type ServerEvent =
    | WsConnectedEvent
    | WsReadyEvent
    | WsSessionsEvent
    | WsSessionReadyEvent
    | WsSessionClosedEvent
    | WsSessionTitleEvent
    | WsHistoryEvent
    | WsMessageStartEvent
    | WsDeltaEvent
    | WsToolStartEvent
    | WsToolDeltaEvent
    | WsToolEndEvent
    | WsMessageEndEvent
    | WsMessageCancelEvent
    | WsUserMessageEvent
    | WsToolRequestEvent
    | WsErrorEvent;

// ─── Client → Server commands ────────────────────────────────────────────────

export type ClientCommand =
    | { type: 'init_complete'; protocolVersion: string; frontendVersion: string; tabId: string }
    | { type: 'declare_tools'; tools: WsToolDeclaration[] }
    | { type: 'declare_blocks'; blocks: WsBlockDeclaration[] }
    | { type: 'tool_result'; toolCallId: string; result: WsToolResultPayload }
    | { type: 'get_sessions'; before?: string; limit?: number }
    | { type: 'create_session' }
    | { type: 'delete_session'; sessionId: string }
    | { type: 'session_title'; sessionId: string; title: string }
    | { type: 'join_session'; sessionId: string }
    | { type: 'leave_session' }
    | { type: 'get_history'; limit?: number; before?: string; after?: string }
    | { type: 'send_message'; text: string; page_context: { url: string; title?: string }; attachments?: { fileId: string }[] }
    | { type: 'cancel' }
    | { type: 'send_audio_start' }
    | { type: 'send_audio_stop'; page_context: { url: string; title?: string }; attachments?: { fileId: string }[] };

export const AGENT_PROTOCOL_VERSION = '1';
export const AGENT_FRONTEND_VERSION = '2026.08.10';

/** Коды ошибок протокола, на которые клиент реагирует по-разному. */
export const AGENT_ERROR_CODE = {
    /** Диалог занят другой вкладкой (другой `tabId`). */
    sessionLocked: 'session_locked',
    /** Диалог перешёл к более новому соединению этой же вкладки, обычно после reload. */
    sessionTakenOver: 'session_taken_over',
    /** Конфигурация агента не соответствует клиенту (версия, протокол, etc.). */
    configMismatch: 'config_mismatch',
} as const;

// ─── DTO ─────────────────────────────────────────────────────────────────────

export interface WsSessionInfo {
    sessionId: string;
    title?: string;
    createdAt: string;
    updatedAt: string;
    locked?: boolean;
}

export interface WsHistoryAttachment {
    fileId: string;
    name: string;
    mime: string;
    size: number;
    kind: string;
}

export interface WsHistoryMessage {
    messageId: string;
    role: 'user' | 'assistant';
    text: string;
    blocks?: WsBlock[];
    attachments?: WsHistoryAttachment[];
    timestamp: string;
}

export interface WsBlock {
    name: string;
    data: Record<string, unknown>;
}

/** Декларация блока для передачи агенту через `declare_blocks`. */
export interface WsBlockDeclaration {
    name: string;
    description: string;
    schema: Record<string, unknown>;
}

/** Декларация frontend-инструмента для передачи агенту через `declare_tools`. */
export interface WsToolDeclaration {
    toolName: string;
    description: string;
    requestFormat: Record<string, unknown>;
    responseFormat: Record<string, unknown>;
    compact_parameters?: boolean;
    compact_outputs?: boolean;
    compact_grouping?: string[];
}

/** Результат выполнения frontend-инструмента. */
export type WsToolResultPayload =
    | { ok: true; payload: unknown }
    | { ok: false; error: { code: string; message: string } };
